/**
 * THU-72 deviation 1 — the host's display name, SUGGESTION side.
 *
 * The worker never guesses a host's name: an invite that names the wrong person
 * fails the whole point of naming one (be obviously not-spam) while looking
 * like it worked, so outbound copy uses only a name the host actually saved.
 *
 * That leaves the feature shipping dark for every host who never opens
 * Settings, which is where this belongs instead. A guess is fine in a text box
 * the host is looking at — they can see it, fix it, or clear it, and nothing
 * leaves the building until they press Save. Guess where it is cheap to
 * correct; never where it is already on its way to somebody else.
 *
 * Returns '' when the address yields nothing that reads like a first name,
 * which is the common case for role mailboxes and handles.
 */

/** Mailboxes that are a function, not a person. "Info picked Casablanca" is
 * worse than saying nothing. */
const NOT_A_PERSON = new Set([
  'admin',
  'billing',
  'contact',
  'email',
  'hello',
  'help',
  'hi',
  'info',
  'mail',
  'me',
  'movie',
  'movies',
  'night',
  'nights',
  'noreply',
  'no-reply',
  'office',
  'postmaster',
  'sales',
  'support',
  'team',
  'thursday',
  'user',
  'webmaster',
]);

export function suggestDisplayName(email: string | null | undefined): string {
  const at = (email ?? '').trim().toLowerCase().split('@');
  if (at.length !== 2 || !at[1]) return '';

  // "john.wood+movies@" → "john". A plus-tag is routing, never a name, and the
  // first dotted segment is the given name in every first.last convention.
  const first = at[0].split('+')[0].split(/[._-]/).filter(Boolean)[0] ?? '';

  // Letters only, and at least three of them. A handle with digits ("mrbig92")
  // is not a name to put in front of "picked", and a two-letter local part is
  // initials far more often than it is a name — "jw@" is John Wood, not "Jw".
  // This does cost Al, Ed and Bo their suggestion; they type two characters
  // into a box that is already in front of them, which is the cheaper mistake.
  if (!/^[a-z]{3,20}$/.test(first)) return '';
  if (NOT_A_PERSON.has(first)) return '';

  return first[0].toUpperCase() + first.slice(1);
}
